import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Sparkles, X, Send, Loader2 } from 'lucide-react';
import { sendChat } from '../services/api';

const SESSION_KEY = 'pdxmap_chat_session';
const SUGGESTIONS = [
  'Show me fintech startups in Portland',
  'Which biotech companies are in Corvallis?',
  'Any climate startups in Bend?',
  'Tell me about 3PO Networks',
];

// Very light markdown-ish rendering: **bold** + bullet lines + line breaks.
const renderText = (text) =>
  text.split('\n').map((line, i) => {
    const trimmed = line.trim();
    const bullet = /^[-*•]\s+/.test(trimmed);
    const content = bullet ? trimmed.replace(/^[-*•]\s+/, '') : line;
    const parts = content.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith('**') && p.endsWith('**')
        ? <strong key={j} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
        : <span key={j}>{p}</span>
    );
    return (
      <div key={i} className={bullet ? 'flex gap-1.5 pl-1' : ''}>
        {bullet && <span className="text-slate-400 mt-0.5">•</span>}
        <span>{parts}</span>
      </div>
    );
  });

const ChatWidget = ({ open, setOpen }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(localStorage.getItem(SESSION_KEY) || null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const ask = useCallback(async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setLoading(true);
    try {
      const data = await sendChat({ message: q, sessionId: sessionRef.current });
      if (data.session_id) {
        sessionRef.current = data.session_id;
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: 'Sorry, I had trouble answering that. Please try again in a moment.',
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading]);

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-launcher-button"
          aria-label="Ask Turoczy AI"
          className="hidden sm:inline-flex fixed bottom-6 right-4 z-[960] items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white pl-3.5 pr-4 py-3 shadow-xl shadow-slate-900/25 transition-all hover:-translate-y-0.5 active:translate-y-0"
        >
          <Sparkles size={18} className="text-amber-300" />
          <span className="text-[13px] font-semibold hidden sm:inline">Ask Turoczy AI</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-[970] bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden inset-x-0 bottom-0 h-[75vh] rounded-t-2xl sm:inset-x-auto sm:bottom-6 sm:right-4 sm:h-[560px] sm:w-[380px] sm:rounded-2xl animate-in slide-in-from-bottom-4 fade-in duration-200"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white flex-shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-amber-300" />
              <div>
                <div className="text-[13px] font-semibold leading-tight">Turoczy AI</div>
                <div className="text-[10.5px] text-slate-300 leading-tight">Ask about any startup, sector or city</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close-button"
              className="text-slate-300 hover:text-white p-1 rounded-lg hover:bg-white/10"
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-3.5 py-3 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="pt-2">
                <p className="text-[13px] text-slate-600 leading-relaxed px-1">
                  👋 Hi! I can help you explore startups across Oregon &amp; SW Washington. Try one of these:
                </p>
                <div className="mt-3 space-y-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => ask(s)}
                      data-testid="chat-suggestion"
                      className="block w-full text-left text-[12.5px] text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-xl px-3 py-2 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] text-[12.5px] leading-relaxed rounded-2xl px-3 py-2 whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-slate-900 text-white rounded-br-sm'
                      : 'bg-white text-slate-700 border border-slate-200 rounded-bl-sm'
                  }`}
                >
                  {m.role === 'assistant' ? <div className="space-y-1">{renderText(m.content)}</div> : m.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 inline-flex items-center gap-2 text-[12.5px] text-slate-500">
                  <Loader2 size={14} className="animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); ask(); }}
            className="flex items-center gap-2 p-2.5 border-t border-slate-200 bg-white flex-shrink-0"
          >
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="chat-input"
              placeholder="Ask about startups…"
              className="flex-1 text-[13px] px-3 py-2 bg-slate-100 rounded-full focus:outline-none focus:ring-2 focus:ring-slate-900/10 text-slate-800 placeholder:text-slate-400"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="chat-send-button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white flex-shrink-0"
              aria-label="Send message"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
